import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, LoadingIndicator, Tab, Tabs, Text, TextInput } from "@jsq/ds/core";
import { useAppContext } from "~/contexts/appContext";
import { useGetQueryFieldsForTemplateEditorQuery } from "~/graphql/supergraphHooks";
import { ComingSoon } from "~/pages/Reporting/Settings/components/DataStructure/DataPackageSlideout/QueryDetailTakeover/ComingSoon";
import { FieldsGrid } from "~/pages/Reporting/Settings/components/DataStructure/DataPackageSlideout/QueryDetailTakeover/FieldsGrid";
import { toQueryFieldRows } from "~/pages/Reporting/Settings/components/DataStructure/DataPackageSlideout/QueryDetailTakeover/fieldsMapper";
import { SPACING } from "~/styles/variables";
import { hasGlobalReportingAccess } from "~/utils/userRoles";
import * as styles from "../styles_vanilla.css";

export type QueryReferenceTabProps = {
  queryId: number | null;
  arenaId: number | null;
  dataPackageId: number | null;
  dataStructureBasePath: string;
};

type QueryFieldTab = "data-fields" | "footnotes" | "custom-message";

const FIELD_TAB_CONFIG: { id: QueryFieldTab; label: string }[] = [
  { id: "data-fields", label: "Data fields" },
  { id: "footnotes", label: "Footnotes" },
  { id: "custom-message", label: "Custom message" },
];

export const QueryReferenceTab = ({
  queryId,
  arenaId,
  dataPackageId,
  dataStructureBasePath,
}: QueryReferenceTabProps) => {
  const history = useHistory();
  const { user } = useAppContext();
  const [searchValue, setSearchValue] = useState("");
  const [activeFieldTab, setActiveFieldTab] = useState<QueryFieldTab>("data-fields");

  const { data, loading, refetch } = useGetQueryFieldsForTemplateEditorQuery({
    variables: {
      queryId: queryId ?? 0,
      arenaId: arenaId ?? undefined,
    },
    skip: queryId == null,
    fetchPolicy: "cache-and-network",
  });

  const queryName = data?.finpubQuery?.name ?? "Query";

  const fields = useMemo(
    () => toQueryFieldRows(data?.finpubQuery?.fields).map((row) => row.display),
    [data],
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshQuery = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  const handleUpdateQuery = useCallback(() => {
    if (queryId == null || dataPackageId == null) return;
    const path = `${dataStructureBasePath}/${dataPackageId}/queries/${queryId}/edit`;
    const href = history.createHref({ pathname: path });
    window.open(href, "_blank", "noopener,noreferrer");
  }, [dataPackageId, dataStructureBasePath, history, queryId]);

  const isSystemPackage = dataStructureBasePath.startsWith("/reporting/settings");
  const canUpdateQuery =
    queryId != null &&
    dataPackageId != null &&
    (!isSystemPackage || hasGlobalReportingAccess(user.roles));

  if (queryId == null) {
    return (
      <Flex justifyContent="center" className={styles.queryReferenceEmpty}>
        <Text variant="secondary-md">Select a query to preview its fields.</Text>
      </Flex>
    );
  }

  if (loading && fields.length === 0) {
    return (
      <Flex justifyContent="center" className={styles.queryReferenceEmpty}>
        <LoadingIndicator />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" className={styles.queryReferenceCard}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={SPACING.MEDIUM}
        className={styles.queryReferenceCardHeader}
      >
        <Text variant="h4">{queryName}</Text>
        <Flex alignItems="center" gap={SPACING.SMALL}>
          <Button
            variant="neutral"
            icon="arrows-rotate"
            iconPosition="left"
            onClick={() => void handleRefreshQuery()}
            disabled={isRefreshing}
            data-testid="query-reference-refresh-btn"
          >
            Refresh query
          </Button>
          {canUpdateQuery && (
            <Button
              variant="neutral"
              icon="arrow-up-right-from-square"
              iconPosition="right"
              onClick={handleUpdateQuery}
              data-testid="query-reference-update-btn"
            >
              Update query
            </Button>
          )}
        </Flex>
      </Flex>

      <Tabs className={styles.queryReferenceFieldTabs} hasPaddingInline={false}>
        {FIELD_TAB_CONFIG.map(({ id, label }) => (
          <Tab
            key={id}
            data-testid={`query-reference-tab-${id}`}
            active={activeFieldTab === id}
            text={label}
            onClick={() => setActiveFieldTab(id)}
          />
        ))}
      </Tabs>

      {activeFieldTab === "data-fields" && (
        <>
          <Flex className={styles.queryReferenceToolbar}>
            <TextInput
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search name"
              isClearable
              onClearValue={() => setSearchValue("")}
              icon="search"
              data-testid="query-reference-fields-search"
              disabled={fields.length === 0}
            />
          </Flex>

          <div className={styles.queryReferenceGridContainer}>
            <FieldsGrid fields={fields} searchValue={searchValue} isViewMode />
          </div>
        </>
      )}

      {activeFieldTab === "footnotes" && (
        <ComingSoon
          title="Footnotes coming soon"
          description="You'll be able to manage query footnotes here."
        />
      )}

      {activeFieldTab === "custom-message" && (
        <ComingSoon title="Custom messages coming soon" />
      )}
    </Flex>
  );
};
